import React from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  space,
  border,
  layout,
  flexbox,
  position,
  color,
  typography
} from 'styled-system';
import Box from './Box';
import Flex from './Flex';
import Text from './Text';

const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 100%; }
`;

const slideIn = keyframes`
  0% { left: calc(-100%); }
  100% { left: 0; }
`;

const animation = props =>
  css`
    ${props.asDrawer ? slideIn : fadeIn} 0.3s;
  `;

const animationFlex = props =>
  css`
    ${props.asDrawer ? fadeIn : undefined} 0.3s;
  `;

const SysModal = styled(Box)(
  {
    boxShadow:
      'rgba(40, 42, 52, 0.7) 0px 0px 0px 100vh, rgba(40, 42, 52, 0.7) 0px 0px 32px',
    animation: animation
  },
  space,
  border,
  layout,
  flexbox,
  position,
  color,
  typography
);

const FlexModal = styled(Flex)`
  animation: ${animationFlex};
`;

const backDropColor = 'rgb(27,29,41,0.2)';
export default class Modal extends React.Component {
  state = { isOpen: !!this.props.isOpen };

  static getDerivedStateFromProps(props, state) {
    if (props.isOpen !== state.propIsOpen) {
      return { isOpen: props.isOpen, propIsOpen: props.isOpen };
    }
    return null;
  }

  componentDidMount() {
    if (this.props.isOpen) this.addGlobals();
  }

  componentDidUpdate(prevProps, prevState) {
    const { onClose } = this.props;

    const isOpen = this.state.isOpen;
    const wasOpen = prevState.isOpen;

    if (!wasOpen && isOpen) {
      this.addGlobals();
    } else if (wasOpen && !isOpen) {
      this.removeGlobals();
      onClose && onClose();
    }
  }

  componentWillUnmount() {
    this.removeGlobals();
  }

  toggleOpen = e => {
    this.setState(({ isOpen }) => {
      return { isOpen: !isOpen };
    });
  };

  onEscape = e => {
    if (e.key === 'Escape') {
      this.toggleOpen();
    }
  };

  addGlobals = () => {
    window.document.body.className = 'no-scroll';
    window.document.addEventListener('keyup', this.onEscape);
  };

  removeGlobals = () => {
    window.document.body.className = '';
    window.document.removeEventListener('keyup', this.onEscape);
  };

  renderPortal(children, renderProps, rest = {}) {
    const { asDrawer, hideCloseX, onClick, ...modalProps } = rest;
    return ReactDOM.createPortal(
      <FlexModal
        asDrawer={asDrawer}
        alignItems={asDrawer ? 'flex-start' : 'center'}
        justifyContent="flex-start"
        flexDirection="column"
        bg={[asDrawer ? backDropColor : 'white', backDropColor]}
        position="fixed"
        width={1}
        height="100%"
        top={0}
        right={0}
        left={0}
        style={{ overflowY: 'auto', zIndex: 25 }}
        onClick={e => {
          onClick && onClick(e);
        }}
      >
        <SysModal
          asDrawer={asDrawer}
          marginTop={[0, 9]}
          marginBottom={[0, 7]}
          borderRadius={[0, '4px']}
          minHeight={['100vh', 'auto']}
          backgroundColor={'white'}
          maxWidth={'100vw'}
          zIndex={25}
          width={[1 / 2, 1 / 2, 1 / 2, 0.45]}
          minWidth={[asDrawer ? '180px' : '100vw', '560px']}
          mx={asDrawer ? 0 : 'auto'}
          boxShadow={[asDrawer ? 'modalOverlay' : 'none', 'modalOverlay']}
          onClick={e => {
            onClick && onClick(e);
          }}
          aria-modal="true"
          data-testid="modal"
          {...modalProps}
        >
          {!asDrawer && !hideCloseX && (
            <Box
              onClick={this.toggleOpen}
              as="button"
              bg="transparent"
              border="none"
              p={0}
              position="absolute"
              top="0"
              right="8px"
              zIndex="22"
            >
              <Text.displayLargeHeader
                color="grayscale.charcoal"
                fontWeight="regular"
                css={`
                  cursor: pointer;
                  line-height: 1;
                `}
                data-testid="modal-close"
              >
                &times;
              </Text.displayLargeHeader>
            </Box>
          )}
          {typeof children === 'function' ? children(renderProps) : children}
        </SysModal>
      </FlexModal>,
      window.document.getElementById('portal-root')
    );
  }

  render() {
    const { activator, children, isOpen: _isOpen, ...rest } = this.props;
    const { isOpen } = this.state;
    const renderProps = { isOpen, toggleOpen: this.toggleOpen };

    return (
      <>
        {activator && activator(renderProps)}
        {isOpen && this.renderPortal(children, renderProps, rest)}
      </>
    );
  }
}
